import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, qs } from "../api";
import { Alert, Badge, Button, Card, DataTable, Empty, ErrorBox, FormError, Loading, Modal, PageHeader, Reg, TextInput, useDebounced, useToast } from "../ui";
import { CustomerPicker, VehicleFinder, type CustomerLite, DeleteRecordButton } from "../components";
import { DeptBadge, JobStatusBadge, date } from "../format";
import { useIsOwner } from "../auth";
import { FilesPanel } from "../extras";

export function VehicleList() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") ?? "");
  const dq = useDebounced(q.trim());
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ["vehicles", dq], queryFn: () => api.get(`/vehicles${qs({ q: dq })}`) });
  return (
    <>
      <PageHeader title="Vehicles" sub="Search saved vehicles first — current and previous registrations are both matched." actions={<Button variant="primary" onClick={() => setAdding(true)}>+ Add vehicle</Button>} />
      <div className="filters"><input className="input" style={{ maxWidth: 380 }} placeholder="Registration, make or model" value={q} onChange={(e) => { setQ(e.target.value); setParams(e.target.value ? { q: e.target.value } : {}, { replace: true }); }} aria-label="Search vehicles" /></div>
      <Card pad={false}>
        {list.isLoading ? <Loading /> : list.isError ? <ErrorBox error={list.error} retry={() => list.refetch()} /> : (
          <DataTable rows={list.data.vehicles} rowLink={(v: any) => `/vehicles/${v.id}`} empty={<Empty title={dq ? "No saved vehicle matches" : "No vehicles yet"} action={<Button variant="primary" onClick={() => setAdding(true)}>Add a vehicle</Button>} />}
            columns={[
              { key: "r", label: "Registration", render: (v: any) => <><Reg reg={v.registration} />{v.matched_previous_reg && <div className="small muted">was {v.matched_previous_reg}</div>}</> },
              { key: "m", label: "Vehicle", render: (v: any) => [v.year, v.make, v.model].filter(Boolean).join(" ") || "—" },
              { key: "o", label: "Owner", render: (v: any) => v.owner_name ?? <span className="muted">Not linked</span> },
              { key: "l", label: "Last job", render: (v: any) => (v.last_job_at ? date(v.last_job_at.slice(0, 10)) : "—") },
            ]} />
        )}
      </Card>
      {adding && <AddVehicleModal onClose={() => setAdding(false)} onDone={(id) => navigate(`/vehicles/${id}`)} />}
    </>
  );
}

function AddVehicleModal({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [customer, setCustomer] = useState<CustomerLite | null>(null);
  return (
    <Modal title="Add vehicle" onClose={onClose} wide>
      <div className="stack">
        <CustomerPicker value={customer} onChange={setCustomer} label="Owner (optional)" />
        <VehicleFinder customer={customer} onSelected={(v) => onDone(v.id)} />
      </div>
    </Modal>
  );
}

export function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const owner = useIsOwner();
  const q = useQuery({ queryKey: ["vehicle", id], queryFn: () => api.get(`/vehicles/${id}`) });
  const [modal, setModal] = useState<null | "owner" | "reg" | "edit">(null);
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const { vehicle: v, currentOwner, previousOwners, registrations, jobs } = q.data;
  return (
    <>
      <PageHeader title={<><Reg reg={v.registration} /> {[v.make, v.model].filter(Boolean).join(" ")}</>} back={{ to: "/vehicles", label: "Vehicles" }}
        sub={[v.variant, v.year, v.fuel, v.engine_cc ? `${v.engine_cc} cc` : null, v.colour].filter(Boolean).join(" · ") || "Details not recorded"}
        actions={<>
          <Button onClick={() => setModal("edit")}>Edit details</Button>
          <Button onClick={() => setModal("owner")}>Change owner</Button>
          <Button onClick={() => setModal("reg")}>Correct / change plate</Button>
          <DeleteRecordButton kind="vehicle" id={v.id} onDone={() => navigate("/vehicles")} />
          {currentOwner && <Link className="btn btn-primary" to={`/jobs/new?customer=${currentOwner.customer_id}&vehicle=${v.id}`}>New job</Link>}
        </>} />
      <div className="grid grid-sidebar">
        <Card title="Work history" pad={false}>
          <DataTable rows={jobs} rowLink={(j: any) => (j.customer_id || owner ? `/jobs/${j.id}` : undefined) as any}
            empty={<Empty title="No previous work recorded" />}
            columns={[
              { key: "d", label: "Date", render: (j: any) => date(j.created_at.slice(0, 10)) },
              { key: "n", label: "Job", render: (j: any) => j.number },
              { key: "c", label: "Customer", render: (j: any) => j.previousOwner ? <Badge>{j.customer_name}</Badge> : j.customer_name },
              { key: "dep", label: "Dept", render: (j: any) => <DeptBadge d={j.department} /> },
              { key: "m", label: "Mileage", num: true, render: (j: any) => j.mileage_in?.toLocaleString("en-GB") ?? "—" },
              { key: "w", label: "Work", render: (j: any) => j.symptoms ?? "" },
              { key: "s", label: "Status", render: (j: any) => <JobStatusBadge s={j.status} /> },
            ]} />
        </Card>
        <div className="stack">
          <Card title="Current owner">
            {currentOwner ? <><Link to={`/customers/${currentOwner.customer_id}`}><strong>{currentOwner.name}</strong></Link><div className="small muted">Since {date(currentOwner.from_date)}</div></> : <span className="muted">No owner linked</span>}
            {previousOwners.length > 0 && <div className="small muted" style={{ marginTop: 8 }}>{previousOwners.length} previous owner{previousOwners.length > 1 ? "s" : ""}{!owner && " (details hidden)"}</div>}
          </Card>
          <Card title="Registration history">
            <ul className="timeline">{registrations.map((r: any) => <li key={r.registration + r.from_date}><Reg reg={r.registration} /> <span className="small muted">{date(r.from_date)} – {r.to_date ? date(r.to_date) : "now"}</span></li>)}</ul>
          </Card>
          <FilesPanel ownerType="vehicle" ownerId={v.id} title="Vehicle photos" category="vehicle" />
          <Card title="Data source">
            <div className="small">{v.data_source === "manual" ? "Entered manually" : v.data_source === "enquiry" ? "Added from an enquiry (details not checked yet)" : v.data_source}</div>
          </Card>
        </div>
      </div>
      {modal === "owner" && <ChangeOwnerModal vehicleId={v.id} onClose={() => setModal(null)} />}
      {modal === "reg" && <ChangePlateModal vehicleId={v.id} current={v.registration} onClose={() => setModal(null)} />}
      {modal === "edit" && <EditVehicleModal v={v} onClose={() => setModal(null)} />}
    </>
  );
}

function ChangeOwnerModal({ vehicleId, onClose }: { vehicleId: string; onClose: () => void }) {
  const [customer, setCustomer] = useState<CustomerLite | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => api.post(`/vehicles/${vehicleId}/owner`, { customerId: customer!.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vehicle", vehicleId] }); toast("success", "Owner updated"); onClose(); },
  });
  return (
    <Modal title="Change owner" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!customer} loading={m.isPending} onClick={() => m.mutate()}>Save new owner</Button></>}>
      <Alert kind="info">Past jobs stay linked to the previous owner. Staff will see the work history but not the previous owner's name or contact details.</Alert>
      <div style={{ marginTop: 12 }}><CustomerPicker value={customer} onChange={setCustomer} label="New owner" /></div>
      <FormError error={m.error} />
    </Modal>
  );
}

function ChangePlateModal({ vehicleId, current, onClose }: { vehicleId: string; current: string; onClose: () => void }) {
  const [reg, setReg] = useState("");
  const [mode, setMode] = useState<"correction" | "change">("correction");
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => api.post(`/vehicles/${vehicleId}/registration`, { registration: reg, correction: mode === "correction" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vehicle", vehicleId] }); qc.invalidateQueries({ queryKey: ["vehicles"] }); toast("success", mode === "correction" ? "Registration corrected" : "Registration changed"); onClose(); },
  });
  const other = m.error instanceof ApiError ? m.error.details?.vehicleId : null;
  return (
    <Modal title="Registration" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={reg.replace(/\s/g, "").length < 2} loading={m.isPending} onClick={() => m.mutate()}>Save</Button></>}>
      <div className="stack">
        <div>Current: <Reg reg={current} /></div>
        <label className="check"><input type="radio" name="plate-mode" checked={mode === "correction"} onChange={() => setMode("correction")} /> <span><strong>Correct a typing mistake</strong><br /><span className="small muted">The wrong plate is replaced. Job cards not yet invoiced show the corrected plate; issued invoices keep what was printed.</span></span></label>
        <label className="check"><input type="radio" name="plate-mode" checked={mode === "change"} onChange={() => setMode("change")} /> <span><strong>The car's plate has changed</strong> (private plate, re-registration)<br /><span className="small muted">The old plate is kept in the history and the car can still be found by it.</span></span></label>
        <TextInput label={mode === "correction" ? "Correct registration" : "New registration"} className="reg-input" value={reg} onChange={(e) => setReg(e.target.value.toUpperCase())} />
      </div>
      <FormError error={m.error} />
      {other && <Link className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} to={`/vehicles/${other}`} onClick={onClose}>Open the vehicle that has this plate</Link>}
    </Modal>
  );
}

function EditVehicleModal({ v, onClose }: { v: any; onClose: () => void }) {
  const [f, setF] = useState({ make: v.make ?? "", model: v.model ?? "", variant: v.variant ?? "", year: v.year ? String(v.year) : "", fuel: v.fuel ?? "", engineCc: v.engine_cc ? String(v.engine_cc) : "", colour: v.colour ?? "", vin: v.vin ?? "", notes: v.notes ?? "" });
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => api.patch(`/vehicles/${v.id}`, { ...f, year: f.year ? Number(f.year) : null, engineCc: f.engineCc ? Number(f.engineCc) : null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vehicle", v.id] }); toast("success", "Vehicle updated"); onClose(); },
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title="Edit vehicle" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Save</Button></>}>
      <div className="form-grid">
        <TextInput label="Make" value={f.make} onChange={set("make")} />
        <TextInput label="Model" value={f.model} onChange={set("model")} />
        <TextInput label="Variant / trim" value={f.variant} onChange={set("variant")} />
        <TextInput label="Year" inputMode="numeric" value={f.year} onChange={set("year")} />
        <TextInput label="Fuel" value={f.fuel} onChange={set("fuel")} />
        <TextInput label="Engine (cc)" inputMode="numeric" value={f.engineCc} onChange={set("engineCc")} />
        <TextInput label="Colour" value={f.colour} onChange={set("colour")} />
        <TextInput label="VIN" value={f.vin} onChange={set("vin")} />
        <TextInput label="Notes" full value={f.notes} onChange={set("notes")} />
      </div>
      <FormError error={m.error} />
    </Modal>
  );
}
